import * as React from 'react';
import * as _ from 'lodash';
import InputAdornment, {InputAdornmentProps} from 'original/InputAdornment';
import IconButton from 'original/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import {createStyles, Theme, withStyles, WithStyles} from 'original/styles';
import MuiInput, {InputProps as MuiInputProps} from 'original/Input/Input';
import {useRef} from 'react';
import useDidMount from '@rooks/use-did-mount';

interface InputProps extends MuiInputProps {
  clearable?: boolean;
  units?: string | {value: string; position: 'start' | 'end'};
  autoSelect?: boolean;
}

export type ExtendedInputProps = InputProps & Partial<WithStyles<typeof styles>>;

const styles = (theme: Theme) =>
  createStyles({
    closeIcon: {
      marginLeft: theme.spacing(-7),
      padding: theme.spacing(0.5),
    },
    positionEnd: {
      marginLeft: 0,
    },
  });

export default withStyles(styles)((({
  clearable,
  units,
  autoSelect,
  onChange,
  value,
  classes: {closeIcon, positionEnd},
  endAdornment,
  startAdornment,
  ...props
}) => {
  const inputRef = useRef(null);
  useDidMount(() => {
    if (autoSelect && inputRef.current) {
      inputRef.current.select();
    }
  });
  const unitsValue = units && !_.isString(units) ? units.value : units;
  const unitsPosition = units && !_.isString(units) ? units.position : 'end';
  const startAdornmentContent = (
    <React.Fragment>
      {startAdornment ? (startAdornment as React.ReactElement<InputAdornmentProps>).props.children : undefined}
      {unitsValue && unitsPosition === 'start' ? unitsValue : undefined}
    </React.Fragment>
  );
  const endAdornmentContent = (
    <React.Fragment>
      {endAdornment ? (endAdornment as React.ReactElement<InputAdornmentProps>).props.children : undefined}
      {unitsValue && unitsPosition === 'end' ? unitsValue : undefined}
      {clearable && !_.isEmpty(value) ? (
        <IconButton className={closeIcon} onClick={() => onChange(null)}>
          <CloseIcon fontSize="small" />
        </IconButton>
      ) : (
        undefined
      )}
    </React.Fragment>
  );
  const renderStartAdornment = !_.isEmpty(_.filter(React.Children.toArray(startAdornmentContent.props.children)));
  const renderEndAdornment = !_.isEmpty(_.filter(React.Children.toArray(endAdornmentContent.props.children)));
  return (
    <MuiInput
      inputRef={inputRef}
      endAdornment={
        renderEndAdornment ? (
          <InputAdornment position="end" className={positionEnd}>
            {endAdornmentContent}
          </InputAdornment>
        ) : (
          undefined
        )
      }
      startAdornment={
        renderStartAdornment ? <InputAdornment position="start">{startAdornmentContent}</InputAdornment> : undefined
      }
      onChange={onChange}
      value={value}
      {...props}
    />
  );
}) as React.FunctionComponent<ExtendedInputProps>);
